import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { logger } from '@/lib/logger';
import { PublicFloorPlanCanvas } from '@/components/floor-plans/PublicFloorPlanCanvas';

interface PublicFloorPlanData {
  floorPlan: {
    id: string;
    name: string;
    image_url: string;
    scale_data?: any;
  };
  fixtures: any[];
  circuits: any[];
  controls: any[];
  rooms: any[];
  panels: any[];
}

function PublicFloorPlanViewer() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PublicFloorPlanData | null>(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    setError(null);

    // Get Supabase configuration from environment - NEVER hardcode production keys
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !anonKey) {
      logger.error('Missing required environment variables for public floor plan viewer');
      setError('Configuration error: Unable to load floor plan. Please contact support.');
      setLoading(false);
      return;
    }

    const url = `${supabaseUrl}/functions/v1/public-floor-plan?token=${token}`;
    fetch(url, {
      headers: {
        apikey: anonKey,
        Authorization: `Bearer ${anonKey}`,
      },
      mode: 'cors',
    })
      .then(async res => {
        if (res.status === 404)
          throw new Error('This link is invalid or the floor plan was not found.');
        if (res.status === 410) throw new Error('This public link has expired.');
        if (!res.ok) throw new Error('Failed to load floor plan.');
        return res.json();
      })
      .then(json => {
        setData(json);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [token]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-background">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <span className="text-muted-foreground">Loading shared floor plan…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-background">
        <div className="text-destructive text-lg font-semibold mb-2">{error}</div>
        <div className="text-muted-foreground">
          If you believe this is a mistake, please contact the sender.
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-background">
        <div className="text-muted-foreground">No data available</div>
      </div>
    );
  }

  // Simple read-only viewer - just display the floor plan image for now
  return (
    <div className="flex flex-col w-full h-screen bg-background">
      {/* Notification banner at the top */}
      <div className="w-full bg-primary/90 text-white text-center py-3 px-4 text-sm font-medium">
        <div className="flex items-center justify-center gap-2">
          <span>📋 {data.floorPlan.name}</span>
          <span className="text-white/70">•</span>
          <span className="text-white/90">Read-only shared view</span>
        </div>
      </div>

      {/* Main content container with floor plan canvas */}
      <div className="relative flex-1 w-full overflow-hidden">
        {data.floorPlan.image_url ? (
          <PublicFloorPlanCanvas
            floorPlan={data.floorPlan}
            fixtures={data.fixtures}
            controls={data.controls}
            rooms={data.rooms}
            panels={data.panels}
          />
        ) : (
          <div className="flex items-center justify-center h-full bg-gray-100 text-muted-foreground">
            No floor plan image available
          </div>
        )}
      </div>
    </div>
  );
}

export default PublicFloorPlanViewer;
